import React from "react";

export const formatMoney = (value, rate = 1) => {
  const n = (Number(value) || 0) * (rate || 1);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatNumber = (value) => {
  const n = Number(value) || 0;
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
};

export function displayValue(field, rec, { rate = 1, optionLabel } = {}) {
  const v = rec ? rec[field.key] : undefined;
  if (field.type === "number") return field.currency ? formatMoney(v, rate) : formatNumber(v);
  if (Array.isArray(v)) return v.join(", ");
  if (optionLabel) return optionLabel(field, v);
  return v === undefined || v === null || v === "" ? "--" : String(v);
}

const STATUS_LABEL = { new: "New", dirty: "Edited" };

// The list every simple database page shows: tick boxes, one column per
// field, and, on money columns, the BidLight value underneath when it
// differs from the row's own.
export const DatabaseTable = ({
  fields,
  items,
  rate,
  activeKey,
  onRowClick,
  onRowDoubleClick,
  onToggle,
  onToggleAll,
  optionLabel,
  emptyText,
  extraColumns = [],
  showStatus = true,
  showIsPublic = false,
  onIsPublic,
  onIsPublicAll,
}) => {
  const cols = fields.filter((f) => f.type !== "hidden");
  const allSelected = items.length > 0 && items.every((it) => it.selected);
  // BaseDatabaseModel "Is Public" column with its header toggle (BidLight
  // admin, company scope).
  const allPublic = items.length > 0 && items.every((it) => !!it.rec.isPublic);
  return (
    <div className="table-responsive" style={{ maxHeight: "calc(100 * var(--vh) - 330px)", overflow: "auto" }}>
      <table className="table table-sm table-hover mb-0" data-testid="database-table">
        <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
          <tr>
            <th style={{ width: 36 }}>
              <input
                type="checkbox"
                className="squared-checkbox"
                aria-label="Select all"
                checked={allSelected}
                onChange={(e) => onToggleAll(e.target.checked)}
              />
            </th>
            {showIsPublic && (
              <th className="f-14" style={{ width: 80 }}>
                <label className="d-flex align-items-center gap-1 mb-0">
                  <input
                    type="checkbox"
                    className="squared-checkbox"
                    aria-label="All public"
                    checked={allPublic}
                    onChange={(e) => onIsPublicAll && onIsPublicAll(e.target.checked)}
                  />
                  Is Public
                </label>
              </th>
            )}
            {cols.map((f) => (
              <th key={f.key} className="f-14">
                {f.label}
              </th>
            ))}
            {extraColumns.map((c) => (
              <th key={c.key} className="f-14">
                {c.label}
              </th>
            ))}
            <th style={{ width: 70 }} />
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr>
              <td colSpan={cols.length + extraColumns.length + 2 + (showIsPublic ? 1 : 0)} className="text-center text-muted py-4">
                {emptyText || "No records for this version."}
              </td>
            </tr>
          )}
          {items.map((it) => (
            <tr
              key={it.key}
              data-testid="database-row"
              className={it.key === activeKey ? "table-active" : ""}
              style={{ cursor: "pointer" }}
              onClick={() => onRowClick && onRowClick(it.key)}
              onDoubleClick={() => onRowDoubleClick && onRowDoubleClick(it.key)}
            >
              <td onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  className="squared-checkbox"
                  aria-label="Select row"
                  checked={!!it.selected}
                  onChange={(e) => onToggle(it.key, e.target.checked)}
                />
              </td>
              {showIsPublic && (
                <td onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className="squared-checkbox"
                    aria-label="Is public"
                    checked={!!it.rec.isPublic}
                    onChange={(e) => onIsPublic && onIsPublic(it.key, e.target.checked)}
                  />
                </td>
              )}
              {cols.map((f) => {
                const own = displayValue(f, it.rec, { rate, optionLabel });
                const db = it.db && f.currency ? displayValue(f, it.db, { rate }) : null;
                return (
                  <td key={f.key} className="f-14" style={{ whiteSpace: f.type === "textarea" ? "pre-wrap" : "nowrap" }}>
                    {own}
                    {db !== null && db !== own && (
                      <div className="text-muted" style={{ fontSize: 11 }} title="BidLight value">
                        BidLight: {db}
                      </div>
                    )}
                  </td>
                );
              })}
              {extraColumns.map((c) => (
                <td key={c.key} className="f-14">
                  {c.render(it)}
                </td>
              ))}
              <td className="f-12 text-muted">{showStatus ? STATUS_LABEL[it.status] || "" : ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
